import * as SwitchPrimitive from '@radix-ui/react-switch'
import { cn } from '@/lib/utils'

export interface ToggleProps {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
  label?: string
  description?: string
  id?: string
  className?: string
}

/**
 * on/off の設定行トグル。
 * Acl.tsx / Admin.tsx のチェックボックス（`h-4 w-4 rounded border-gray-300`）は
 * フォーム入力向けで、独立した ON/OFF 設定（アクセス許可の有効化等）には
 * スイッチの方が意図が伝わりやすいため、既存 devDependency の
 * `@radix-ui/react-switch` を初めて配線する。
 * トーンは既存の `bg-primary`（ON）/ `bg-muted`（OFF）に揃える。
 */
export function Toggle({
  checked,
  onCheckedChange,
  disabled,
  label,
  description,
  id,
  className,
}: ToggleProps) {
  const switchId = id ?? (label ? `toggle-${label.replace(/\s+/g, '-')}` : undefined)

  const control = (
    <SwitchPrimitive.Root
      id={switchId}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-60',
        checked ? 'bg-primary' : 'bg-muted',
        className,
      )}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'block h-4 w-4 rounded-full bg-background shadow-sm transition-transform',
          checked ? 'translate-x-[18px]' : 'translate-x-[2px]',
        )}
      />
    </SwitchPrimitive.Root>
  )

  if (!label) {
    return control
  }

  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <label htmlFor={switchId} className="text-sm font-medium">
          {label}
        </label>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      {control}
    </div>
  )
}
